import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Plus, FileText, Eye, Pause, Play, Trash2, Edit, MoreVertical, AlertTriangle,
    Mail, Megaphone, Send, ArrowLeft, TrendingUp, BarChart3, ChevronRight
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
    getAdvertiserListings, getAdvertiserStats, updateListingStatus, deleteListing,
    getAdvertiserMessages, getAdvertiserConversation, advertiserReplyMessage,
    getAdvertiserAnnouncements, getAdvertiserUnreadCount
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';

const DashboardPage = () => {
    const { advertiser, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('listings');
    const [listings, setListings] = useState([]);
    const [stats, setStats] = useState({ total_listings: 0, active_listings: 0, on_hold_listings: 0, total_views: 0 });
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [listingToDelete, setListingToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Messages state
    const [messages, setMessages] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [replyContent, setReplyContent] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    
    // Announcements state
    const [announcements, setAnnouncements] = useState([]);

    useEffect(() => {
        if (!authLoading && !advertiser && !localStorage.getItem('hmc_token')) {
            navigate('/advertiser/login');
        }
    }, [advertiser, authLoading, navigate]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [listingsRes, statsRes, messagesRes, announcementsRes, unreadRes] = await Promise.all([
                    getAdvertiserListings(),
                    getAdvertiserStats(),
                    getAdvertiserMessages().catch(() => ({ data: [] })),
                    getAdvertiserAnnouncements().catch(() => ({ data: [] })),
                    getAdvertiserUnreadCount().catch(() => ({ data: { unread_count: 0 } }))
                ]);
                setListings(listingsRes.data);
                setStats(statsRes.data);
                setMessages(messagesRes.data);
                setAnnouncements(announcementsRes.data);
                setUnreadCount(unreadRes.data.unread_count);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        if (advertiser) {
            fetchData();
        }
    }, [advertiser]);

    const handleStatusChange = async (listingId, newStatus) => {
        try {
            await updateListingStatus(listingId, newStatus);
            setListings(listings.map(l => l.id === listingId ? { ...l, status: newStatus } : l));
            toast.success(`Listing ${newStatus === 'active' ? 'activated' : 'put on hold'}`);
        } catch (error) {
            toast.error('Failed to update listing status');
        }
    };

    const openDeleteDialog = (listing) => {
        setListingToDelete(listing);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!listingToDelete) return;
        
        setDeleting(true);
        try {
            await deleteListing(listingToDelete.id);
            setListings(listings.filter(l => l.id !== listingToDelete.id));
            setStats(prev => ({
                ...prev,
                total_listings: Math.max(0, prev.total_listings - 1),
                active_listings: listingToDelete.status === 'active' ? Math.max(0, prev.active_listings - 1) : prev.active_listings
            }));
            toast.success('Listing deleted successfully');
            setDeleteDialogOpen(false);
            setListingToDelete(null);
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete listing. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    // Messages functions
    const openConversation = async (conversationId) => {
        try {
            const res = await getAdvertiserConversation(conversationId);
            setSelectedConversation(res.data);
            setConversationMessages(res.data.messages);
            // Update unread count
            const unreadRes = await getAdvertiserUnreadCount();
            setUnreadCount(unreadRes.data.unread_count);
        } catch (error) {
            toast.error('Failed to load conversation');
        }
    };

    const handleReply = async () => {
        if (!replyContent.trim() || !selectedConversation) return;
        
        setSendingReply(true);
        try {
            await advertiserReplyMessage(selectedConversation.conversation_id, { content: replyContent });
            toast.success('Reply sent');
            setReplyContent('');
            // Refresh conversation
            openConversation(selectedConversation.conversation_id);
            // Refresh messages list
            const res = await getAdvertiserMessages();
            setMessages(res.data);
        } catch (error) {
            toast.error('Failed to send reply');
        } finally {
            setSendingReply(false);
        }
    };

    // Calculate top performing listings
    const topListings = [...listings]
        .filter(l => l.status === 'active')
        .sort((a, b) => b.views - a.views)
        .slice(0, 5);

    if (authLoading || !advertiser) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen" data-testid="dashboard-page">
                {/* Header */}
                <div className="bg-navy text-white py-8">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                                    Welcome, {advertiser.first_name}!
                                </h1>
                                <p className="text-gray-300 mt-1">{advertiser.business_name}</p>
                            </div>
                            <Link to="/dashboard/new-listing">
                                <Button className="bg-gold hover:bg-amber-600" data-testid="new-listing-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Post New Ad
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-gray-500">Total Listings</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-navy">{stats.total_listings}</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-gray-500">Active Listings</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-green-600">{stats.active_listings}</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-gray-500">On Hold</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-yellow-600">{stats.on_hold_listings}</div>
                            </CardContent>
                        </Card>
                        <Card className={unreadCount > 0 ? 'border-amber-500 border-2' : ''}>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                    <Eye className="w-4 h-4" /> Total Views
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-gold">{stats.total_views}</div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Announcements Banner */}
                    {announcements.length > 0 && (
                        <div className="mb-6 space-y-3">
                            {announcements.slice(0, 2).map(ann => (
                                <div 
                                    key={ann.id} 
                                    className={`rounded-lg p-4 border-l-4 ${
                                        ann.priority === 'urgent' ? 'bg-red-50 border-red-500' :
                                        ann.priority === 'important' ? 'bg-amber-50 border-amber-500' :
                                        'bg-blue-50 border-blue-500'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <Megaphone className={`w-5 h-5 mt-0.5 ${
                                            ann.priority === 'urgent' ? 'text-red-600' :
                                            ann.priority === 'important' ? 'text-amber-600' :
                                            'text-blue-600'
                                        }`} />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-semibold text-gray-900">{ann.title}</h3>
                                                {ann.priority !== 'normal' && (
                                                    <Badge variant={ann.priority === 'urgent' ? 'destructive' : 'default'} className="text-xs">
                                                        {ann.priority}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-700">{ann.content}</p>
                                            <p className="text-xs text-gray-500 mt-2">{formatDate(ann.created_at)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Membership Badge */}
                    <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4 mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-green-800 font-semibold">
                                    Membership: FREE
                                </span>
                                <p className="text-sm text-green-600 mt-1">Unlimited ads included - no fees, ever!</p>
                            </div>
                            <Badge className="bg-green-500 text-white">Active</Badge>
                        </div>
                    </div>

                    {/* Tabs */}
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                        <TabsList>
                            <TabsTrigger value="listings">
                                <FileText className="w-4 h-4 mr-2" />
                                Listings
                            </TabsTrigger>
                            <TabsTrigger value="analytics">
                                <BarChart3 className="w-4 h-4 mr-2" />
                                Analytics
                            </TabsTrigger>
                            <TabsTrigger value="messages" className="relative">
                                <Mail className="w-4 h-4 mr-2" />
                                Messages
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                                        {unreadCount}
                                    </span>
                                )}
                            </TabsTrigger>
                        </TabsList>

                        {/* Listings Tab */}
                        <TabsContent value="listings">
                            <div className="bg-white rounded-lg shadow-sm">
                                <div className="p-4 border-b flex justify-between items-center">
                                    <h2 className="font-semibold text-lg">Your Listings</h2>
                                    <span className="text-sm text-gray-500">{listings.length} listings</span>
                                </div>

                                {loading ? (
                                    <div className="p-8 text-center">
                                        <div className="spinner mx-auto" />
                                    </div>
                                ) : listings.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                        <h3 className="font-semibold text-gray-900 mb-2">No listings yet</h3>
                                        <p className="text-gray-500 mb-4">Create your first listing to start advertising</p>
                                        <Link to="/dashboard/new-listing">
                                            <Button className="bg-gold hover:bg-amber-600">
                                                <Plus className="w-4 h-4 mr-2" />
                                                Post Your First Ad
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Title</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Category</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Views</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Posted</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {listings.map((listing) => (
                                                    <tr key={listing.id} className="hover:bg-gray-50" data-testid={`listing-row-${listing.id}`}>
                                                        <td className="px-4 py-4">
                                                            <Link to={`/listing/${listing.id}`} className="font-medium text-navy hover:text-gold">
                                                                {listing.title}
                                                            </Link>
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-gray-600">
                                                            {listing.category_name}
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <Badge className={getStatusColor(listing.status)}>
                                                                {listing.status}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-gray-600">
                                                            <div className="flex items-center gap-1">
                                                                <Eye className="w-4 h-4" />
                                                                {listing.views}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 text-sm text-gray-500">
                                                            {formatDate(listing.created_at)}
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" size="sm" data-testid={`actions-${listing.id}`}>
                                                                        <MoreVertical className="w-4 h-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem asChild>
                                                                        <Link to={`/dashboard/edit-listing/${listing.id}`} className="flex items-center gap-2">
                                                                            <Edit className="w-4 h-4" />
                                                                            Edit
                                                                        </Link>
                                                                    </DropdownMenuItem>
                                                                    {listing.status === 'active' ? (
                                                                        <DropdownMenuItem
                                                                            onClick={() => handleStatusChange(listing.id, 'on_hold')}
                                                                            className="flex items-center gap-2"
                                                                        >
                                                                            <Pause className="w-4 h-4" />
                                                                            Put on Hold
                                                                        </DropdownMenuItem>
                                                                    ) : (
                                                                        <DropdownMenuItem
                                                                            onClick={() => handleStatusChange(listing.id, 'active')}
                                                                            className="flex items-center gap-2"
                                                                        >
                                                                            <Play className="w-4 h-4" />
                                                                            Activate
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    <DropdownMenuItem
                                                                        onClick={() => openDeleteDialog(listing)}
                                                                        className="flex items-center gap-2 text-red-600"
                                                                        data-testid={`delete-${listing.id}`}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                        Delete
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        {/* Analytics Tab */}
                        <TabsContent value="analytics">
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Performance Overview */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <TrendingUp className="w-5 h-5 text-green-600" />
                                            Performance Overview
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                <span className="text-gray-600">Total Views</span>
                                                <span className="text-xl font-bold text-navy">{stats.total_views}</span>
                                            </div>
                                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                <span className="text-gray-600">Active Ads</span>
                                                <span className="text-xl font-bold text-green-600">{stats.active_listings}</span>
                                            </div>
                                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                <span className="text-gray-600">Avg. Views/Listing</span>
                                                <span className="text-xl font-bold text-amber-600">
                                                    {stats.total_listings > 0 ? Math.round(stats.total_views / stats.total_listings) : 0}
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Top Performing Listings */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <BarChart3 className="w-5 h-5 text-amber-600" />
                                            Top Performing Listings
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {topListings.length > 0 ? (
                                            <div className="space-y-3">
                                                {topListings.map((listing, index) => (
                                                    <div key={listing.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                            index === 0 ? 'bg-amber-100 text-amber-700' :
                                                            index === 1 ? 'bg-gray-200 text-gray-700' :
                                                            index === 2 ? 'bg-orange-100 text-orange-700' :
                                                            'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            {index + 1}
                                                        </span>
                                                        <div className="flex-1 min-w-0">
                                                            <Link to={`/listing/${listing.id}`} className="font-medium text-sm text-navy hover:text-gold truncate block">
                                                                {listing.title}
                                                            </Link>
                                                            <p className="text-xs text-gray-500">{listing.category_name}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
                                                            <Eye className="w-4 h-4" />
                                                            {listing.views}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-center py-4">No active listings yet</p>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Quick Stats */}
                                <Card className="md:col-span-2">
                                    <CardHeader>
                                        <CardTitle>Listing Status Breakdown</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                                <div className="text-3xl font-bold text-green-600">{stats.active_listings}</div>
                                                <p className="text-sm text-green-700 mt-1">Active</p>
                                            </div>
                                            <div className="text-center p-4 bg-yellow-50 rounded-lg">
                                                <div className="text-3xl font-bold text-yellow-600">{stats.on_hold_listings}</div>
                                                <p className="text-sm text-yellow-700 mt-1">On Hold</p>
                                            </div>
                                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                                <div className="text-3xl font-bold text-gray-600">{stats.total_listings}</div>
                                                <p className="text-sm text-gray-700 mt-1">Total</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        {/* Messages Tab */}
                        <TabsContent value="messages">
                            <div className="grid md:grid-cols-3 gap-4">
                                {/* Conversations List */}
                                <Card className="md:col-span-1">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-lg">Inbox</CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        <div className="divide-y max-h-[500px] overflow-y-auto">
                                            {messages.map(msg => (
                                                <div 
                                                    key={msg.conversation_id}
                                                    className={`p-3 cursor-pointer hover:bg-gray-50 ${
                                                        selectedConversation?.conversation_id === msg.conversation_id ? 'bg-blue-50' : ''
                                                    } ${msg.unread_count > 0 ? 'border-l-4 border-amber-500 bg-amber-50/50' : ''}`}
                                                    onClick={() => openConversation(msg.conversation_id)}
                                                    data-testid={`conversation-${msg.conversation_id}`}
                                                >
                                                    <div className="flex justify-between items-start mb-1">
                                                        <p className="font-semibold text-sm text-navy">{msg.subject}</p>
                                                        {msg.unread_count > 0 && (
                                                            <Badge className="bg-amber-500 text-xs">{msg.unread_count}</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-600 truncate">{msg.last_message}</p>
                                                    <p className="text-xs text-gray-400 mt-1">{formatDate(msg.last_date)}</p>
                                                </div>
                                            ))}
                                            {messages.length === 0 && (
                                                <div className="p-8 text-center">
                                                    <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                                    <p className="text-sm text-gray-500">No messages yet</p>
                                                    <p className="text-xs text-gray-400 mt-1">Messages from admin will appear here</p>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Conversation Detail */}
                                <Card className="md:col-span-2">
                                    <CardHeader className="pb-2 border-b">
                                        {selectedConversation ? (
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <CardTitle className="text-lg">{conversationMessages[0]?.subject}</CardTitle>
                                                    <p className="text-sm text-gray-500">Conversation with HMC Admin</p>
                                                </div>
                                                <Button variant="ghost" size="sm" onClick={() => setSelectedConversation(null)}>
                                                    <ArrowLeft className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <CardTitle className="text-lg text-gray-400">Select a conversation</CardTitle>
                                        )}
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        {selectedConversation ? (
                                            <>
                                                <div className="h-[350px] overflow-y-auto p-4 space-y-3">
                                                    {conversationMessages.map(msg => (
                                                        <div 
                                                            key={msg.id}
                                                            className={`flex ${msg.sender_type === 'advertiser' ? 'justify-end' : 'justify-start'}`}
                                                        >
                                                            <div className={`max-w-[80%] rounded-lg p-3 ${
                                                                msg.sender_type === 'advertiser' 
                                                                    ? 'bg-navy text-white' 
                                                                    : 'bg-gray-100'
                                                            }`}>
                                                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                                <p className={`text-xs mt-1 ${
                                                                    msg.sender_type === 'advertiser' ? 'text-gray-300' : 'text-gray-500'
                                                                }`}>
                                                                    {msg.sender_name} • {formatDate(msg.created_at)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="p-4 border-t">
                                                    <div className="flex gap-2">
                                                        <Textarea
                                                            placeholder="Type your reply..."
                                                            value={replyContent}
                                                            onChange={(e) => setReplyContent(e.target.value)}
                                                            rows={2}
                                                            className="flex-1"
                                                            data-testid="reply-input"
                                                        />
                                                        <Button 
                                                            onClick={handleReply} 
                                                            disabled={sendingReply || !replyContent.trim()}
                                                            className="bg-navy hover:bg-navy/90"
                                                            data-testid="send-reply-btn"
                                                        >
                                                            <Send className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="h-[400px] flex items-center justify-center text-gray-400">
                                                <div className="text-center">
                                                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                    <p>Select a conversation to view messages</p>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="w-5 h-5" />
                                Delete Listing
                            </DialogTitle>
                            <DialogDescription>
                                Are you sure you want to delete &quot;{listingToDelete?.title}&quot;? This action cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="flex gap-2 sm:gap-0">
                            <Button
                                variant="outline"
                                onClick={() => setDeleteDialogOpen(false)}
                                disabled={deleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={deleting}
                                data-testid="confirm-delete-btn"
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default DashboardPage;
